// Unauthenticated entry point — redesign edition. Editorial cover with
// Plex-Mono scenario label, Plex-Serif hero title, ink-on-cream form.
//
// Five modes:
//   login    → email + password + captcha → /api/auth/login
//   register → email + name + password + invite + captcha → /api/auth/register
//   confirm  → 6-digit code → /api/auth/verify
//   forgot   → email → /api/auth/forgot (not yet wired backend; UI shows
//              friendly placeholder until the endpoint lands)
//   reset    → new password (from email link token) → /api/auth/reset
//              (same backend-not-yet-wired note)
//
// Captcha: self-hosted Altcha (PoW). No Cloudflare in customer path.
// onLogin(user) is called on success; parent flips into the main app.
import { useState, useEffect } from "react";
import { api, setToken } from "../api.js";
import { T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import { Btn, Inp, Mono, Serif } from "./ui.jsx";
import { useT, LangProvider } from "../i18n.jsx";

// Legal pages by locale: English at /{kind}.html, Russian at
// /policy/{kind}.html, Spanish at /es/{kind}.html. Anything else → English.
function policyHref(kind, lang) {
  if (lang === "ru") return `/policy/${kind}.html`;
  if (lang === "es") return `/es/${kind}.html`;
  return `/${kind}.html`;
}
import { AltchaWidget } from "./Altcha.jsx";

// localStorage key for the pre-login language pick. Persists across
// page reloads so a returning user doesn't have to re-pick every time
// they hit /login.
const AUTH_LANG_KEY = "bg_auth_lang";
const SUPPORTED_LANGS = ["en", "ru", "es"];

function detectInitialLang() {
  // Priority: explicit user pick (localStorage) > browser → 'en' default.
  try {
    const stored = localStorage.getItem(AUTH_LANG_KEY);
    if (SUPPORTED_LANGS.includes(stored)) return stored;
  } catch {}
  try {
    const browser = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (SUPPORTED_LANGS.includes(browser)) return browser;
  } catch {}
  return "en";
}

// Outer wrapper — owns the pre-login language pick and wraps the form
// in LangProvider so useT() inside the form resolves to the chosen
// locale. Refactored from a single component because useT() called in
// the same scope as the LangProvider can't see the provider above it.
export function AuthScreen({ onLogin }) {
  const [lang, setLang] = useState(detectInitialLang);
  const pickLang = (next) => {
    setLang(next);
    try { localStorage.setItem(AUTH_LANG_KEY, next); } catch {}
  };
  return (
    <LangProvider lang={lang}>
      <AuthScreenInner onLogin={onLogin} lang={lang} onPickLang={pickLang} />
    </LangProvider>
  );
}

function AuthScreenInner({ onLogin, lang, onPickLang }) {
  const t = useT();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("ref") || ""; }
    catch { return ""; }
  });
  const [code, setCode] = useState("");
  const [demoCode, setDemoCode] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [altchaSolution, setAltchaSolution] = useState(null);
  const [altchaReset, setAltchaReset] = useState(0);
  // Single agreement checkbox — covers Privacy Policy + Terms of
  // Service. Submit stays disabled until checked. The previous
  // three-checkbox 152-ФЗ pattern was retired when the operator
  // moved to a US-incorporated international SaaS structure.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // 0051 fields — confirm password, phone, nickname. Phone is
  // E.164-shaped (+CC...); the form keeps user input verbatim and
  // only normalises on submit. Nickname state holds the lowercased
  // value the API sees; the displayed value is the user's typing,
  // re-lowercased on each keystroke.
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [nickname, setNickname] = useState("");
  // Live nickname availability — null = unknown / not yet checked,
  // true / false = checked. checking flag drives the spinner.
  const [nicknameAvailable, setNicknameAvailable] = useState(null);
  const [nicknameChecking, setNicknameChecking] = useState(false);

  // Debounced nickname check. Cancels on every keystroke and only
  // hits the API when the user has stopped typing for 400ms.
  useEffect(() => {
    if (mode !== "register") return;
    const v = nickname.trim().toLowerCase();
    if (!v) { setNicknameAvailable(null); return; }
    // Front-end gate matching the server-side rules — saves a
    // round-trip for obviously-bad input.
    if (!/^[a-z0-9_]{3,30}$/.test(v)) {
      setNicknameAvailable(false);
      return;
    }
    setNicknameChecking(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.checkNickname(v);
        setNicknameAvailable(!!r?.available);
      } catch {
        setNicknameAvailable(null);
      } finally {
        setNicknameChecking(false);
      }
    }, 400);
    return () => { clearTimeout(handle); setNicknameChecking(false); };
  }, [nickname, mode]);

  // Phone validity — local-only. We accept user input with spaces,
  // dashes, parens; only the stripped value needs to match E.164.
  const normalizedPhone = phone.replace(/[\s\-()]/g, "");
  const phoneValid = /^\+[1-9]\d{6,14}$/.test(normalizedPhone);

  // Aggregate: every condition that gates the submit button.
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canRegister = acceptedTerms && passwordsMatch && phoneValid
                       && nicknameAvailable === true;

  const resetCaptcha = () => { setAltchaSolution(null); setAltchaReset((k) => k + 1); };
  const goto = (next) => { setMode(next); setErr(""); setInfo(""); };

  const handleRegister = async () => {
    setErr(""); setInfo("");
    if (!passwordsMatch) {
      return setErr(t("auth.error.passwordMismatch"));
    }
    if (!phoneValid) {
      return setErr(t("auth.error.phoneFormat"));
    }
    if (nicknameAvailable !== true) {
      return setErr(t("auth.error.pickNickname"));
    }
    if (!acceptedTerms) {
      return setErr(t("auth.error.acceptTerms"));
    }
    if (!altchaSolution) return setErr(t("auth.captchaRequired"));
    setLoading(true);
    try {
      const res = await api.register(email, displayName, password,
        altchaSolution, referralCode.trim() || undefined, {
          acceptedTerms,
        }, {
          phone: normalizedPhone,
          nickname: nickname.trim().toLowerCase(),
          // Pass the auth-screen language pick — server saves it to
          // user.language so the first authed session resolves to
          // the same language they registered in.
          language: lang,
        });
      setPendingEmail(email.toLowerCase().trim());
      if (res.code) setDemoCode(res.code);
      setMode("confirm");
    } catch (e) { setErr(e.message); resetCaptcha(); }
    setLoading(false);
  };

  const handleConfirm = async () => {
    setErr(""); setLoading(true);
    try {
      const res = await api.verify(pendingEmail, code);
      setToken(res.token);
      onLogin(res.user);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const handleLogin = async () => {
    setErr(""); setInfo("");
    if (!altchaSolution) return setErr(t("auth.captchaRequired"));
    setLoading(true);
    try {
      const res = await api.login(email, password, altchaSolution);
      setToken(res.token);
      onLogin(res.user);
    } catch (e) { setErr(e.message); resetCaptcha(); }
    setLoading(false);
  };

  // Forgot/reset — backend endpoints not yet implemented. Show a friendly
  // info message and a fallback path (DM the operator) so customers
  // aren't stuck. Once /api/auth/forgot + /api/auth/reset land, wire
  // these to the real endpoints.
  const handleForgot = async () => {
    setErr(""); setInfo("");
    if (!email) return setErr("Email required");
    setInfo(t("auth.info.forgotPlaceholder"));
  };
  const handleReset = async () => {
    setErr(""); setInfo("");
    setInfo(t("auth.info.resetPlaceholder"));
  };

  return (
    // The global `body { overflow: hidden }` in index.html is needed
    // for the main app's sidebar+chat shell (each pane scrolls
    // internally). AuthScreen has a tall form that needs the page to
    // scroll, so it becomes its OWN scroll container.
    //
    // iOS Safari quirks intentionally addressed here:
    //   - `touch-action: pan-y` tells the engine this surface
    //     receives vertical pans. Without it, the first scroll works
    //     but the next touch hit-tests to a child (input/button/
    //     captcha) and the surface stops responding to scrolls.
    //   - `overscroll-behavior: contain` prevents the bounce at the
    //     boundary from handing the gesture back to the locked body.
    //   - We deliberately do NOT set `-webkit-overflow-scrolling:
    //     touch`. That property was a fix on iOS <13 and is now a
    //     no-op or worse in modern Safari (causes exactly the
    //     "scroll once, stops" symptom this comment exists for).
    <div style={{
      position: "fixed", inset: 0,
      overflowY: "auto",
      touchAction: "pan-y",
      overscrollBehavior: "contain",
      background: T_NEW.paper,
      fontFamily: T_NEW.fontSans,
      color: T_NEW.ink,
    }}>
      <div style={{
        minHeight: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "calc(16px + env(safe-area-inset-top, 0px)) 16px calc(16px + env(safe-area-inset-bottom, 0px))",
        boxSizing: "border-box",
      }}>
      <div style={{ width: 400, maxWidth: "92vw" }}>

        {/* Language switcher — three pills (English / Español /
            Русский), sized small and placed above the editorial
            cover so it doesn't compete with the hero. Pick persists
            via localStorage; on registration it's sent to the API so
            user.language is set at signup and the first authed
            session resolves to the same language. */}
        <LangSwitch lang={lang} onPick={onPickLang} />

        {/* Editorial cover: stamp + wordmark + Serif hero + body. The
            "Сценарий N · ..." Plex-Mono label that used to sit above
            the lockup was removed — it read as internal-design jargon
            rather than something useful to the visitor. */}
        <div style={{ marginBottom: T_NEW.s7, textAlign: "center" }}>
          {/* BuyerGram stamp mark + wordmark. Same SVG used as the app
              icon, just sized to the auth lockup. Loaded as a static
              asset (one HTTP request, browser-cached) so it appears
              instantly without waiting for Plex fonts. */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                        gap: T_NEW.s3, marginBottom: T_NEW.s5 }}>
            <img src="/buyergram-icon-stamp.svg" alt=""
                 width={88} height={88} aria-hidden />
            <img src="/buyergram-wordmark.svg" alt="BuyerGram"
                 height={32} style={{ width: "auto" }} />
          </div>

          <Serif as="h1" size={T_NEW.hero} style={{ marginBottom: T_NEW.s2, lineHeight: 1.1 }}>
            {t(`auth.hero.${mode}`)}
          </Serif>
          <p style={{
            fontSize: T_NEW.body, color: T_NEW.ink56,
            lineHeight: 1.5, marginTop: T_NEW.s2,
            maxWidth: 340, marginLeft: "auto", marginRight: "auto",
          }}>{t(`auth.tagline.${mode}`)}</p>
        </div>

        {/* Form card */}
        <div style={{
          background: T_NEW.paperRaised,
          borderRadius: T_NEW.r3, padding: T_NEW.s6,
          boxShadow: T_NEW.shadowCard,
          border: `1px solid ${T_NEW.ink12}`,
        }}>
          {mode === "confirm" ? (
            <>
              <div style={{
                background: T_NEW.persimmonSoft, borderRadius: T_NEW.r2,
                padding: T_NEW.s4, marginBottom: T_NEW.s5, textAlign: "center",
              }}>
                <Mono color={T_NEW.persimmon} style={{ display: "block", marginBottom: T_NEW.s1 }}>
                  {t("auth.codeSent")}
                </Mono>
                <div style={{ fontSize: T_NEW.bodyLg, color: T_NEW.ink, fontWeight: 600 }}>{pendingEmail}</div>
                {demoCode && (
                  <div style={{
                    fontSize: T_NEW.meta, color: T_NEW.ink56,
                    marginTop: T_NEW.s3, background: T_NEW.paper,
                    borderRadius: T_NEW.r1, padding: "10px 16px",
                    display: "inline-block", fontFamily: T_NEW.fontMono,
                  }}>
                    DEV CODE <span style={{ color: T_NEW.persimmon, fontWeight: 700, fontSize: T_NEW.displaySm, letterSpacing: 4, marginLeft: 6 }}>{demoCode}</span>
                  </div>
                )}
              </div>
              <Inp icon={I.lock()} label={t("auth.field.codeFromEmail")} value={code} onChange={setCode} placeholder="123456" autoFocus />
              {err && <ErrorLine>{err}</ErrorLine>}
              <Btn full onClick={handleConfirm} disabled={loading}>
                {loading ? t("auth.verifying") : t("auth.btn.confirm")}
              </Btn>
              <ModeSwitch onClick={() => goto("register")}>{t("auth.back.toRegister")}</ModeSwitch>
            </>
          ) : mode === "forgot" ? (
            <>
              <Inp icon={I.mail()} label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" autoFocus />
              {err && <ErrorLine>{err}</ErrorLine>}
              {info && <InfoLine>{info}</InfoLine>}
              <Btn full onClick={handleForgot} disabled={loading}>
                {loading ? t("auth.btn.sending") : t("auth.btn.sendLink")}
              </Btn>
              <ModeSwitch onClick={() => goto("login")}>{t("auth.back.toLogin")}</ModeSwitch>
            </>
          ) : mode === "reset" ? (
            <>
              <Inp icon={I.lock()} label={t("auth.field.newPassword")} type="password" value={password} onChange={setPassword} placeholder="••••••••" autoFocus />
              {err && <ErrorLine>{err}</ErrorLine>}
              {info && <InfoLine>{info}</InfoLine>}
              <Btn full onClick={handleReset} disabled={loading}>
                {loading ? t("auth.btn.saving") : t("auth.btn.savePassword")}
              </Btn>
              <ModeSwitch onClick={() => goto("login")}>{t("auth.back.toLogin")}</ModeSwitch>
            </>
          ) : (
            <>
              {mode === "register" && (
                <Inp icon={I.users()} label={t("auth.field.fullName")}
                     value={displayName} onChange={setDisplayName}
                     placeholder={t("auth.field.fullName.ph")} autoFocus
                     autoComplete="name" />
              )}
              {mode === "register" && (
                <Inp icon={I.users()} label={t("auth.field.nickname")}
                     value={nickname}
                     onChange={(v) => setNickname(
                       v.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 30)
                     )}
                     placeholder={t("auth.field.nickname.ph")}
                     autoComplete="username"
                     rightSlot={<NicknameStatus
                       value={nickname}
                       checking={nicknameChecking}
                       available={nicknameAvailable}
                     />} />
              )}
              <Inp icon={I.mail()} label={t("auth.email")} type="email"
                   value={email} onChange={setEmail}
                   placeholder="you@example.com"
                   autoFocus={mode === "login"}
                   autoComplete={mode === "register" ? "email" : "username"} />
              {mode === "register" && (
                <Inp icon={I.users()} label={t("auth.field.phone")}
                     type="tel" inputMode="tel"
                     value={phone} onChange={setPhone}
                     placeholder={t("auth.field.phone.ph")}
                     autoComplete="tel" />
              )}
              <Inp icon={I.lock()} label={t("auth.password")} type="password"
                   value={password} onChange={setPassword}
                   placeholder="••••••••"
                   autoComplete={mode === "register"
                     ? "new-password" : "current-password"} />
              {mode === "register" && (
                <Inp icon={I.lock()} label={t("auth.field.confirmPassword")}
                     type="password"
                     value={confirmPassword}
                     onChange={setConfirmPassword}
                     placeholder="••••••••"
                     autoComplete="new-password"
                     error={confirmPassword.length > 0 && !passwordsMatch
                       ? t("auth.error.passwordsDontMatch") : ""} />
              )}
              {mode === "register" && (
                <Inp icon={I.users()} label={t("auth.field.inviteCode")} value={referralCode} onChange={(v) => setReferralCode(v.toUpperCase())} placeholder="ABCD1234" />
              )}
              {mode === "register" && (
                <div style={{
                  marginTop: T_NEW.s3, padding: T_NEW.s3,
                  background: T_NEW.paperSoft,
                  border: `1px solid ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r1,
                }}>
                  <TermsCheckbox
                    checked={acceptedTerms}
                    onChange={setAcceptedTerms}
                    lang={lang}
                  />
                </div>
              )}
              <AltchaWidget onSolution={setAltchaSolution} resetKey={altchaReset} />
              {err && <ErrorLine>{err}</ErrorLine>}
              <Btn full onClick={mode === "register" ? handleRegister : handleLogin}
                   disabled={loading || (mode === "register" && !canRegister)}>
                {loading ? t("auth.btn.justAMoment") : mode === "register" ? t("auth.createAccount") : t("auth.signIn")}
              </Btn>

              {mode === "login" && (
                <div style={{ textAlign: "center", marginTop: T_NEW.s3 }}>
                  <button onClick={() => goto("forgot")}
                          style={{ background: "none", border: "none", color: T_NEW.persimmon,
                                   fontSize: T_NEW.meta, cursor: "pointer", fontWeight: 500,
                                   fontFamily: T_NEW.fontSans }}>
                    {t("auth.btn.forgotPassword")}
                  </button>
                </div>
              )}

              <div style={{ textAlign: "center", marginTop: T_NEW.s4, paddingTop: T_NEW.s4, borderTop: `1px solid ${T_NEW.ink12}` }}>
                <span style={{ color: T_NEW.ink56, fontSize: T_NEW.body, marginRight: 6 }}>
                  {mode === "register" ? t("auth.alreadyHave") : t("auth.noAccount")}
                </span>
                <button onClick={() => goto(mode === "register" ? "login" : "register")}
                        style={{ background: "none", border: "none", color: T_NEW.ink,
                                 fontSize: T_NEW.body, cursor: "pointer", fontWeight: 600,
                                 fontFamily: T_NEW.fontSans, textDecoration: "underline",
                                 textDecorationColor: T_NEW.persimmon, textUnderlineOffset: 3 }}>
                  {mode === "register" ? t("auth.signIn") : t("auth.toggleRegister")}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Discreet footer — legal links on every auth screen */}
        <div style={{
          marginTop: T_NEW.s5, textAlign: "center",
          fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.ink38,
          letterSpacing: T_NEW.monoTrack, textTransform: "uppercase",
        }}>
          <a href={policyHref("privacy", lang)} target="_blank" rel="noopener noreferrer"
             style={{ color: "inherit", textDecoration: "none", marginRight: T_NEW.s3 }}>{t("auth.terms.privacy")}</a>
          <a href={policyHref("terms", lang)} target="_blank" rel="noopener noreferrer"
             style={{ color: "inherit", textDecoration: "none" }}>{t("auth.terms.tos")}</a>
        </div>
      </div>
      </div>
    </div>
  );
}

// Small inline helpers (kept private to the auth screen)
// Live availability indicator for the nickname field. Three states:
//   empty → nothing (let the input feel uncluttered until the user
//           starts typing)
//   checking → small dot pulse so the user knows we're working
//   available true → green ✓
//   available false → persimmon ✗
function NicknameStatus({ value, checking, available }) {
  const t = useT();
  if (!value || !value.trim()) return null;
  if (checking) {
    return (
      <span style={{
        width: 8, height: 8, borderRadius: 999,
        background: T_NEW.ink38,
        animation: "cg-pulse 1s ease-in-out infinite",
      }} aria-label={t("auth.verifying")} />
    );
  }
  if (available === true) {
    return <span style={{ color: T_NEW.bottle, fontWeight: 700,
                         fontSize: 18, lineHeight: 1 }}>✓</span>;
  }
  if (available === false) {
    return <span style={{ color: T_NEW.persimmon, fontWeight: 700,
                         fontSize: 18, lineHeight: 1 }}>✗</span>;
  }
  return null;
}

// Single agreement checkbox at sign-up. Inline links to Privacy +
// Terms — both required, both open in new tabs so the form state
// doesn't get lost. Native input for accessibility; the row label
// toggles the box, the inline links don't.
function TermsCheckbox({ checked, onChange, lang }) {
  const t = useT();
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      fontSize: T_NEW.meta, color: T_NEW.ink, lineHeight: 1.45,
      cursor: "pointer",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: T_NEW.persimmon,
                 width: 16, height: 16, flexShrink: 0 }}
      />
      <span>
        {t("auth.terms.prefix")}{" "}
        <a href={policyHref("privacy", lang)} target="_blank" rel="noopener noreferrer"
           onClick={(e) => e.stopPropagation()}
           style={{ color: T_NEW.persimmon, textDecoration: "underline" }}>
          {t("auth.terms.privacy")}
        </a>{" "}{t("auth.terms.and")}{" "}
        <a href={policyHref("terms", lang)} target="_blank" rel="noopener noreferrer"
           onClick={(e) => e.stopPropagation()}
           style={{ color: T_NEW.persimmon, textDecoration: "underline" }}>
          {t("auth.terms.tos")}
        </a>.
      </span>
    </label>
  );
}

function ErrorLine({ children }) {
  return (
    <div style={{
      color: T_NEW.danger, fontSize: T_NEW.meta,
      background: T_NEW.dangerSoft, padding: "8px 10px",
      borderRadius: T_NEW.r1, marginBottom: T_NEW.s3,
      fontFamily: T_NEW.fontSans,
    }}>{children}</div>
  );
}
function InfoLine({ children }) {
  return (
    <div style={{
      color: T_NEW.ink, fontSize: T_NEW.meta,
      background: T_NEW.persimmonSoft, padding: "8px 10px",
      borderRadius: T_NEW.r1, marginBottom: T_NEW.s3,
      fontFamily: T_NEW.fontSans,
    }}>{children}</div>
  );
}
// LangSwitch — three pills at the top of the auth screen (English /
// Español / Русский) so unauthenticated visitors can pick the
// language they want before logging in or registering. The active
// pill is filled with the accent color; inactive pills are
// transparent with the accent outline. The label uses the language's
// own endonym so users recognize their language regardless of which
// locale is currently active.
function LangSwitch({ lang, onPick }) {
  const opts = [
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "ru", label: "Русский" },
  ];
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 6,
                  marginBottom: T_NEW.s5 }}>
      {opts.map(({ code, label }) => {
        const active = lang === code;
        return (
          <button key={code} type="button" onClick={() => onPick(code)}
                  style={{
                    background: active ? T_NEW.accent : "transparent",
                    color: active ? "#fff" : T_NEW.accent,
                    border: `1px solid ${T_NEW.accent}`,
                    borderRadius: 999,
                    padding: "5px 12px",
                    fontSize: 12, fontWeight: 600,
                    fontFamily: T_NEW.fontSans,
                    cursor: "pointer",
                  }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ModeSwitch({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: "none", border: "none", color: T_NEW.ink56,
      fontSize: T_NEW.meta, marginTop: T_NEW.s3,
      cursor: "pointer", display: "block", width: "100%", textAlign: "center",
      fontFamily: T_NEW.fontSans,
    }}>{children}</button>
  );
}
